import React, { useEffect, useState } from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../styles/theme';

/**
 * FE-232 — 429 `invite_cooldown` modal with live countdown.
 *
 * Surfaces the SG-17 v1 rate-limit (2 declines in 30d → 48h cooldown
 * per (inviter, invitee_email, group) triple). The modal renders
 * the remaining time live so the user can see when they'll be able
 * to re-invite, instead of having to mentally calculate from a
 * raw timestamp toast.
 *
 * Props:
 *   visible:   whether to show. Parent toggles based on 429 response.
 *   email:     who the cooldown is against (echoed in the body copy).
 *   expiresAt: ISO timestamp from `context.cooldown_expires_at`.
 *   declineCount: how many declines triggered the cooldown (typically 2,
 *                 surfaced for transparency so the user knows it's not
 *                 a one-off).
 *   onClose:   close the modal (no other action — there's nothing the
 *              user can do until the cooldown elapses).
 */
interface InviteCooldownModalProps {
  visible: boolean;
  email: string | null;
  expiresAt: string | null;
  declineCount?: number | null;
  onClose: () => void;
}

const pluralUnit = (n: number, unit: string) =>
  `${n} ${unit}${n === 1 ? '' : 's'}`;

const formatRemaining = (ms: number): string => {
  if (ms <= 0) return 'any moment now';
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;

  if (days >= 1) {
    return `${pluralUnit(days, 'day')} · ${pluralUnit(hours, 'hour')}`;
  }
  if (hours >= 1) {
    return `${pluralUnit(hours, 'hour')} · ${pluralUnit(minutes, 'minute')}`;
  }
  if (minutes >= 1) {
    return `${pluralUnit(minutes, 'minute')} · ${pluralUnit(seconds, 'second')}`;
  }
  return pluralUnit(seconds, 'second');
};

export const InviteCooldownModal: React.FC<InviteCooldownModalProps> = ({
  visible,
  email,
  expiresAt,
  declineCount,
  onClose,
}) => {
  // Tick the remaining-time string every second while visible. Stopping
  // the interval the moment the modal closes avoids leaking timers on
  // navigation. Re-stamps `now` so the formatter sees the new delta.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!visible) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [visible]);

  // Defensive parsing — if BE didn't send expires_at, fall back to a
  // generic message so the modal isn't broken.
  const remainingMs = (() => {
    if (!expiresAt) return null;
    const t = new Date(expiresAt).getTime();
    if (Number.isNaN(t)) return null;
    return Math.max(0, t - now);
  })();
  const friendlyRemaining =
    remainingMs == null ? null : formatRemaining(remainingMs);

  // When the timer hits zero we close the modal automatically so the
  // user can immediately retry — no manual dismiss needed.
  useEffect(() => {
    if (visible && remainingMs != null && remainingMs <= 0) {
      const id = setTimeout(onClose, 600);
      return () => clearTimeout(id);
    }
  }, [visible, remainingMs, onClose]);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.backdrop}>
        <View style={styles.card} accessibilityLabel="Invite paused — cooldown active">
          <View style={styles.iconRow}>
            <MaterialIcons name="pause-circle-outline" size={28} color={fpColors.tertiary} />
            <Text style={styles.title}>Invite paused</Text>
          </View>

          <Text style={styles.body}>
            {email ?? 'This person'} has declined{' '}
            {typeof declineCount === 'number' && declineCount > 0
              ? `${declineCount} recent ${declineCount === 1 ? 'invite' : 'invites'}`
              : 'recent invites'}
            . You can try again in:
          </Text>

          {friendlyRemaining ? (
            <View style={styles.countdownBox}>
              <Text style={styles.countdown}>{friendlyRemaining}</Text>
            </View>
          ) : (
            <View style={styles.countdownBox}>
              <Text style={styles.countdownPending}>
                Cooldown is active — try again later.
              </Text>
            </View>
          )}

          <Text style={styles.hint}>
            Per-recipient cooldown protects people from repeat invites
            they've already declined. It resets automatically when the
            timer expires.
          </Text>

          <Pressable
            onPress={onClose}
            style={({ pressed }) => [
              styles.button,
              pressed && { opacity: 0.85 },
            ]}
            accessibilityRole="button"
            accessibilityLabel="Got it"
            testID="invite-cooldown-modal-close"
          >
            <Text style={styles.buttonText}>Got it</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  card: {
    backgroundColor: fpColors.surface,
    borderRadius: 16,
    padding: 22,
    gap: 14,
    elevation: 12,
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 8 },
  },
  iconRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 18,
    color: fpColors.onSurface,
  },
  body: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: fpColors.onSurface,
    lineHeight: 20,
  },
  countdownBox: {
    backgroundColor: fpColors.tertiaryContainer,
    borderRadius: 10,
    paddingVertical: 14,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  countdown: {
    fontFamily: 'Inter_700Bold',
    fontSize: 22,
    color: fpColors.onTertiaryContainer,
    letterSpacing: 0.3,
    textAlign: 'center',
  },
  countdownPending: {
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    color: fpColors.onTertiaryContainer,
    textAlign: 'center',
  },
  hint: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    lineHeight: 16,
  },
  button: {
    marginTop: 4,
    backgroundColor: fpColors.primary,
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: 'center',
  },
  buttonText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onPrimary,
  },
});

/** Helper — parse a 429 invite_cooldown error payload into modal props.
 *  Returns null when the error isn't a cooldown (caller falls back to
 *  the generic toast). */
export function parseInviteCooldown(err: any): {
  expiresAt: string | null;
  declineCount: number | null;
} | null {
  const code = err?.response?.data?.code ?? err?.code;
  if (code !== 'invite_cooldown') return null;
  const ctx = err?.response?.data?.context ?? {};
  return {
    expiresAt: ctx.cooldown_expires_at ?? null,
    declineCount:
      typeof ctx.decline_count === 'number' ? ctx.decline_count : null,
  };
}
